import { useEffect, useState } from 'react';
import managerService, { type ManagerAgent } from '../../services/manager.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import Modal from '../Modal';

interface Props {
  clientId: string;
  clientName: string;
  /** Currently assigned agent — disabled in the picker so the manager
   *  cannot accidentally re-pick the same agent (the backend rejects this
   *  with 409 anyway, but blocking it client-side is friendlier). */
  currentAgentId: string | null;
  onClose: () => void;
  onReassigned: (summary: { newAgentName: string; reassignedCases: number }) => void;
}

const MIN_REASON = 5;
const MAX_REASON = 500;
/** Sentinel select value for "return this client to the unassigned pool"
 *  (clears the agent) rather than moving them to another agent. */
const POOL = '__pool__';

/**
 * Modal for moving a client's primary agent. Lists every active agent with
 * their current pending workload so the manager can balance load. The
 * backend transactionally:
 *   - updates `identity.users.assigned_agent_id`
 *   - updates `assigned_to` on every active OnboardingCase for this client
 *   - writes one identity.audit_log row with old/new values + reason
 */
export default function ReassignAgentModal({
  clientId,
  clientName,
  currentAgentId,
  onClose,
  onReassigned,
}: Readonly<Props>): JSX.Element {
  const [agents, setAgents] = useState<ManagerAgent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    managerService
      .listAgents()
      .then((data) => {
        if (!cancelled) setAgents(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load agents.'));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    if (!selectedId) {
      setSubmitError('Please select an agent, or choose to return the client to the pool.');
      return;
    }
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON) {
      setSubmitError(`Reason must be at least ${MIN_REASON} characters.`);
      return;
    }
    const toPool = selectedId === POOL;
    setSubmitting(true);
    try {
      const res = await managerService.reassignClientAgent(clientId, {
        new_agent_id: toPool ? null : selectedId,
        reason: trimmed,
      });
      const newAgent = toPool ? null : agents?.find((a) => a.id === selectedId);
      let newAgentName: string;
      if (toPool) newAgentName = 'the unassigned pool';
      else if (newAgent) newAgentName = fullName(newAgent);
      else newAgentName = 'the new agent';
      onReassigned({
        newAgentName,
        reassignedCases: res.reassigned_active_cases,
      });
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err, 'Could not reassign this client.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={onClose} busy={submitting} maxWidth="lg">
        <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
          Reassign agent
        </h2>
        <p className="text-sm mb-4" style={{ color: colors.muted }}>
          Move <span className="font-medium" style={{ color: colors.ink }}>{clientName}</span> to
          a different agent — or return them to the unassigned pool. Active onboarding cases
          follow the choice automatically.
        </p>

        {loadError && (
          <div
            className="rounded-lg p-3 text-sm mb-3"
            style={{
              background: colors.dangerBg,
              color: colors.danger,
              border: `1px solid ${colors.dangerBorder}`,
            }}
            role="alert"
          >
            {loadError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm">
            <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
              New agent
            </span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={agents === null || submitting}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.border}`, color: colors.ink, background: colors.surface }}
            >
              <option value="">{agents === null ? 'Loading agents…' : 'Select an agent'}</option>
              {currentAgentId !== null && (
                <option value={POOL}>— Return to pool (no agent) —</option>
              )}
              {agents
                ? [...agents]
                    .sort((a, b) => fullName(a).localeCompare(fullName(b)))
                    .map((agent) => {
                      const isCurrent = agent.id === currentAgentId;
                      return (
                        <option key={agent.id} value={agent.id} disabled={isCurrent}>
                          {fullName(agent)} · {agent.pending_count} pending · {agent.client_count}{' '}
                          {agent.client_count === 1 ? 'client' : 'clients'}
                          {isCurrent ? ' (current)' : ''}
                        </option>
                      );
                    })
                : null}
            </select>
          </label>

          <label className="block text-sm">
            <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
              Reason{' '}
              <span className="font-normal" style={{ color: colors.mutedSoft }}>
                (5–{MAX_REASON} characters; written to the audit log)
              </span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              minLength={MIN_REASON}
              maxLength={MAX_REASON}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
              placeholder="e.g. Workload rebalance — current agent is at capacity."
            />
          </label>

          {submitError && (
            <div
              className="rounded-lg p-3 text-sm"
              style={{
                background: colors.dangerBg,
                color: colors.danger,
                border: `1px solid ${colors.dangerBorder}`,
              }}
              role="alert"
            >
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{
                background: colors.surface,
                color: colors.inkSoft,
                border: `1px solid ${colors.border}`,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || agents === null}
              className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                background: colors.navy,
                color: '#FFFFFF',
                opacity: submitting ? 0.6 : 1,
                cursor: submitting ? 'wait' : 'pointer',
              }}
            >
              {submitting ? 'Reassigning…' : 'Reassign'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
